import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useColors } from '../context/ThemeContext';
import { hasPageAccess } from '../lib/pageAccess';

const NAV_ITEMS = [
  { label: 'Creator Operations', path: '/creator-operations', icon: '◑', roles: ['owner','va'], access: 'Models Hub' },
  { label: 'Command Center',  path: '/command-center',    icon: '⊞', roles: ['owner'] },
  { label: 'Reels Operations', path: '/reels-operations', icon: '▦', roles: ['owner','va'] },
  { label: 'Tasks',           path: '/tasks',             icon: '✓', roles: ['owner','va','chatter'] },
  { label: 'Chatters CRM',    path: '/chatters',          icon: '◇', roles: ['owner','chatter'] },
  { label: 'Models Hub',      path: '/models-hub',        icon: '◑', roles: ['owner','va'] },
  { label: 'Content OS',      path: '/content-os',        icon: '▦', roles: ['owner','va'] },
  { label: 'Sales Overview',  path: '/sales-overview',    icon: '↗', roles: ['owner'] },
  { label: 'Knowledge',       path: '/knowledge',         icon: '◌', roles: ['owner','va','chatter'] },
  { label: 'Finance',         path: '/finance',           icon: '$', roles: ['owner'] },
  { label: 'Sunday Review',   path: '/review',            icon: '◈', roles: ['owner'] },
  { label: 'Pipeline',        path: '/pipeline',          icon: '○', roles: ['owner'] },
  { label: 'AI Generator',    path: '/ai-content',        icon: '✦', roles: ['owner','va'] },
  { label: 'Reddit Operations',path: '/reddit-operations',icon: '↗', roles: ['owner','va','chatter'] },
  { label: 'Send Alert',      path: '/telegram',          icon: '▷', roles: ['owner','va'] },
  { label: 'Documents',       path: '/documents',         icon: '⎘', roles: ['owner','va','chatter'] },
  { label: 'Model Onboarding',path: '/onboarding',        icon: '⊕', roles: ['owner','va'] },
  { label: 'Settings',        path: '/settings',          icon: '⊙', roles: ['owner'] },
];

function highlight(text, query, C) {
  if (!query) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <span style={{ color: C.text1, fontWeight: 600 }}>{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </span>
  );
}

export default function CommandPalette() {
  const C = useColors();
  const [open, setOpen]         = useState(false);
  const [query, setQuery]       = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef  = useRef(null);
  const listRef   = useRef(null);
  const dialogRef = useRef(null);
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  // Cmd+K / Ctrl+K
  useEffect(() => {
    const openNavigation = () => { setQuery(''); setActiveIdx(0); setOpen(true); };
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => { if (!o) { setQuery(''); setActiveIdx(0); } return !o; });
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    window.addEventListener('cuzo:open-navigation', openNavigation);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('cuzo:open-navigation', openNavigation);
    };
  }, []);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Keep keyboard focus inside the open palette and restore it on close.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const timer = setTimeout(() => inputRef.current?.focus(), 30);
    const trapFocus = event => {
      if (event.key !== 'Tab') return;
      const controls = dialogRef.current?.querySelectorAll('button, input');
      if (!controls?.length) return;
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', trapFocus);
    return () => { clearTimeout(timer); document.removeEventListener('keydown', trapFocus); if (previous?.isConnected) previous.focus(); };
  }, [open]);

  const items = NAV_ITEMS
    .filter(item => item.roles.includes(user?.role))
    .filter(item => hasPageAccess(user?.custom_pages, item.access || item.label))
    .filter(item => !query.trim() || item.label.toLowerCase().includes(query.toLowerCase().trim()));

  const go = (path) => {
    navigate(path);
    setOpen(false);
    setQuery('');
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => {
        const next = Math.min(i + 1, items.length - 1);
        // Scroll into view
        setTimeout(() => {
          listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        }, 0);
        return next;
      });
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => {
        const next = Math.max(i - 1, 0);
        setTimeout(() => {
          listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        }, 0);
        return next;
      });
    }
    if (e.key === 'Enter' && items[activeIdx]) go(items[activeIdx].path);
  };

  useEffect(() => setActiveIdx(0), [query]);

  if (!open || !user) return null;

  const isOnPage = (path) => location.pathname === path;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      paddingTop: '14vh',
    }}>
      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      />

      {/* Palette */}
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="Go to workspace" style={{
        position: 'relative', width: '100%', maxWidth: 540, margin: '0 16px',
        background: C.s1,
        border: `1px solid ${C.borderMd}`,
        borderRadius: 12, overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.24)',
        animation: 'scaleIn 0.14s ease both',
      }}>

        {/* Search row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 16px',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="6.5" cy="6.5" r="4.75" stroke={C.text3} strokeWidth="1.4"/>
            <path d="M10.5 10.5l3 3" stroke={C.text3} strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Go to page..."
            aria-label="Find a workspace"
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: C.text1, fontSize: 15, fontFamily: 'Outfit, sans-serif',
              caretColor: C.text1,
            }}
          />
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close workspace search" style={{ color: C.text3, background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, padding: '6px 8px' }}>Esc</button>
          </div>
        </div>

        {/* Results */}
        <div ref={listRef} style={{ maxHeight: 380, overflowY: 'auto', padding: '6px 0' }}>
          {items.length === 0 ? (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: C.text3, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}>
              NO RESULTS
            </div>
          ) : (
            <>
              {!query && (
                <div style={{ padding: '4px 16px 8px', fontSize: 9, color: C.text3, letterSpacing: '0.12em', fontFamily: 'JetBrains Mono, monospace' }}>
                  PAGES
                </div>
              )}
              {items.map((item, i) => {
                const active = i === activeIdx;
                const current = isOnPage(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => go(item.path)}
                    onMouseMove={() => setActiveIdx(i)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '9px 14px', cursor: 'pointer',
                      background: active ? C.hover : 'transparent',
                      transition: 'background 0.08s',
                      width: '100%', border: 'none', textAlign: 'left',
                    }}
                  >
                    {/* Icon box */}
                    <div style={{
                      width: 30, height: 30, borderRadius: 6, flexShrink: 0,
                      border: `1px solid ${active ? C.borderMd : C.border}`,
                      background: active ? C.hover : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, color: active ? C.text1 : C.text3,
                      transition: 'all 0.1s',
                    }}>
                      {item.icon}
                    </div>

                    {/* Label */}
                    <span style={{
                      fontSize: 13.5, fontFamily: 'Outfit, sans-serif',
                      color: active ? C.text1 : C.text2,
                      flex: 1,
                    }}>
                      {highlight(item.label, query, C)}
                    </span>

                    {/* Current page badge */}
                    {current && (
                      <span style={{ fontSize: 9, color: C.text3, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.08em' }}>
                        CURRENT
                      </span>
                    )}

                    {/* Enter hint when active */}
                    {active && !current && (
                      <span style={{ fontSize: 9, color: C.text3, fontFamily: 'JetBrains Mono, monospace' }}>
                        ↵
                      </span>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '8px 16px',
          borderTop: `1px solid ${C.border}`,
          display: 'flex', gap: 16,
        }}>
          {[['↑↓', 'navigate'], ['↵', 'open'], ['esc', 'close']].map(([key, label]) => (
            <div key={key} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <kbd style={{ fontSize: 9, color: C.text3, background: C.hover, border: `1px solid ${C.border}`, borderRadius: 3, padding: '2px 5px', fontFamily: 'JetBrains Mono, monospace' }}>{key}</kbd>
              <span style={{ fontSize: 9, color: C.text3, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>{label.toUpperCase()}</span>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', fontSize: 9, color: C.text3, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>
            {items.length} RESULTS
          </div>
        </div>
      </div>
    </div>
  );
}
